import React, { useEffect, useRef } from 'react';
import { Check, CheckCheck, Reply, Smile } from 'lucide-react';

const ConversationView = ({ messages = [], currentUser, formatTime }) => {
  const messagesEndRef = useRef(null);

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  const scrollToBottom = () => {
    messagesEndRef?.current?.scrollIntoView({ behavior: 'smooth' });
  };

  const getMessageStatusIcon = (status) => {
    switch (status) {
      case 'sent':
        return <Check className="w-3 h-3 text-gray-400" />;
      case 'delivered':
        return <CheckCheck className="w-3 h-3 text-gray-400" />;
      case 'read':
        return <CheckCheck className="w-3 h-3 text-blue-500" />;
      default:
        return null;
    }
  };

  const isCurrentUser = (senderId) => {
    return currentUser?.id === senderId;
  };

  const groupMessagesByDate = (messages) => {
    const groups = [];
    let currentGroup = null;
    let currentDate = null;

    messages?.forEach(message => {
      const messageDate = new Date(message?.created_at)?.toDateString();
      
      if (messageDate !== currentDate) {
        if (currentGroup) {
          groups?.push(currentGroup);
        }
        currentGroup = {
          date: messageDate,
          messages: [message]
        };
        currentDate = messageDate;
      } else {
        currentGroup?.messages?.push(message);
      }
    });

    if (currentGroup) {
      groups?.push(currentGroup);
    }

    return groups;
  };

  const messageGroups = groupMessagesByDate(messages);

  return (
    <div className="flex-1 overflow-y-auto p-6 space-y-4 bg-gradient-to-b from-white to-gray-50 dark:from-neutral-900 dark:to-neutral-950 transition-colors">
      {messageGroups?.map((group, groupIndex) => (
        <div key={groupIndex}>
          {/* Date separator */}
          <div className="flex items-center justify-center my-4">
            <div className="bg-gray-100 dark:bg-neutral-800 text-gray-500 dark:text-neutral-400 text-xs px-3 py-1 rounded-full">
              {new Date(group?.date)?.toLocaleDateString('fr-FR', {
                weekday: 'long',
                year: 'numeric',
                month: 'long',
                day: 'numeric'
              })}
            </div>
          </div>

          {/* Messages for this date */}
          {group?.messages?.map((message, messageIndex) => {
            const isCurrent = isCurrentUser(message?.sender_id);
            const showAvatar = !isCurrent && (
              messageIndex === 0 || 
              group?.messages?.[messageIndex - 1]?.sender_id !== message?.sender_id
            );

            return (
              <div
                key={message?.id}
                className={`flex items-end space-x-2 mb-2 ${
                  isCurrent ? 'justify-end' : 'justify-start'
                }`}
              >
                {/* Avatar for other users */}
                {!isCurrent && (
                  <div className="w-8 h-8 rounded-full overflow-hidden flex-shrink-0">
                    {showAvatar ? (
                      <img
                        src={message?.sender?.avatar_url || `/api/placeholder/32/32`}
                        alt={message?.sender?.full_name}
                        className="w-full h-full object-cover"
                      />
                    ) : (
                      <div className="w-full h-full"></div>
                    )}
                  </div>
                )}
                <div className={`max-w-sm lg:max-w-md xl:max-w-lg ${isCurrent ? 'order-2' : ''}`}>
                  {/* Reply reference */}
                  {message?.reply_to_message && (
                    <div className={`mb-1 ${isCurrent ? 'text-right' : 'text-left'}`}>
                      <div className={`inline-flex items-center px-3 py-1 rounded-lg text-xs ${
                        isCurrent ? 'bg-blue-100 text-blue-700 dark:bg-blue-500/20 dark:text-blue-300' : 'bg-gray-100 text-gray-600 dark:bg-neutral-800 dark:text-neutral-300'
                      }`}>
                        <Reply className="w-3 h-3 mr-1" />
                        Réponse à {message?.reply_to_message?.sender?.full_name}: 
                        "{message?.reply_to_message?.content?.substring(0, 30)}..."
                      </div>
                    </div>
                  )}

                  {/* Message bubble */}
                  <div
                    className={`px-4 py-3 rounded-2xl break-words transition-colors ${
                      isCurrent
                        ? 'bg-blue-500 text-white rounded-br-md shadow' :'bg-white dark:bg-neutral-800 text-gray-900 dark:text-neutral-100 rounded-bl-md shadow-sm border border-gray-100 dark:border-neutral-700'
                    } ${message?.is_priority ? 'ring-2 ring-red-300 dark:ring-red-500/50' : ''}`}
                  >
                    {/* Sender name for group chats */}
                    {!isCurrent && showAvatar && (
                      <p className="text-xs text-gray-500 dark:text-neutral-400 mb-1 font-medium">
                        {message?.sender?.full_name}
                      </p>
                    )}

                    {/* Priority flag */}
                    {message?.is_priority && (
                      <div className="flex items-center mb-1">
                        <span className="text-xs px-2 py-0.5 bg-red-100 dark:bg-red-500/20 text-red-600 dark:text-red-300 rounded-full">
                          🚨 Urgent
                        </span>
                      </div>
                    )}

                    {/* Message content */}
                    <p className="text-sm leading-relaxed whitespace-pre-wrap">
                      {message?.content}
                    </p>

                    {/* File attachment */}
                    {message?.file_url && (
                      <div className="mt-2 p-2 bg-black/10 dark:bg-white/10 rounded-lg">
                        <a
                          href={message?.file_url}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="text-xs underline text-blue-600 dark:text-blue-400"
                        >
                          📎 {message?.file_name || 'Fichier joint'}
                        </a>
                      </div>
                    )}

                    {/* Message reactions */}
                    {message?.message_reactions?.length > 0 && (
                      <div className="flex flex-wrap gap-1 mt-2">
                        {message?.message_reactions?.map((reaction, idx) => (
                          <div
                            key={idx}
                            className="inline-flex items-center px-2 py-1 bg-black/10 dark:bg-white/10 rounded-full text-xs"
                          >
                            <span className="mr-1">{reaction?.emoji}</span>
                            <span>1</span>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>

                  {/* Message metadata */}
                  <div className={`flex items-center mt-1 space-x-1 ${
                    isCurrent ? 'justify-end' : 'justify-start'
                  }`}>
                    <span className="text-xs text-gray-500 dark:text-neutral-500">
                      {formatTime?.(message?.created_at)}
                    </span>
                    {isCurrent && (
                      <div className="flex items-center space-x-1">
                        {getMessageStatusIcon(message?.status)}
                      </div>
                    )}
                  </div>
                </div>
                {/* Spacer for current user to push avatar to right */}
                {isCurrent && <div className="w-8"></div>}
              </div>
            );
          })}
        </div>
      ))}
      {messages?.length === 0 && (
        <div className="flex-1 flex items-center justify-center">
          <div className="text-center">
            <Smile className="w-12 h-12 text-gray-300 dark:text-neutral-600 mx-auto mb-4" />
            <p className="text-gray-500 dark:text-neutral-400">Commencez la conversation en envoyant un message</p>
          </div>
        </div>
      )}
      {/* Scroll anchor */}
      <div ref={messagesEndRef} />
    </div>
  );
};

export default ConversationView;